import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { useModalAction } from "src/components/modal-views/context";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import client from "../../api";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { newPaginationInput } from "src/api/types";
import { getErrorMessage } from "src/lib/utils";
import { Table } from "antd";
import moment from "moment";
import { EditIcon } from "src/components/icons/edit-icon";
import { GreenTickIcon } from "src/components/icons/green-tick-icon";

const CardConversionHistory = () => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();

  const { canView, canList, canEdit } = usePermissions(
    permissionConstants.CARD_CONVERSION_RATES
  );
  const canViewConversions = canView || canList;

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [searchParams, setSearchParams] = useState<newPaginationInput>({
    limit: LIST_PAGE_SIZE,
    page: 1,
  });
  const [total, setTotal] = useState(0);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }
  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);
  const { mutate: getCurrencyConversionRateslist, isLoading } = useMutation(
    client.cardConversionRates.getCurrencyConversionRateslist,
    {
      onSuccess: (data: any) => {
        const dataObj = data?.data?.data;
        setTotal(data?.data?.pagination?.total);
        setDataSource(
          dataObj.map((item: any) => ({
            key: item.id,
            id: item.id,
            merchant_id: item.merchant_id,
            src_currency_code: item.src_currency_code,
            dst_currency_code: item.dst_currency_code,
            rate: item.rate,
            margin_percent: item.margin_percent,
            final_rate: item.final_rate,

            updatedAt: item.updatedAt,
          })),
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: syncConversionCurrencyRates, isLoading: isLoadingSync } =
    useMutation(client.cardConversionRates.syncConversionCurrencyRates, {
      onSuccess: () => {
        getCurrencyConversionRateslist(searchParams); // ✅ refetch
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
        setIsAlertOpened(true);
      },
    });

  const { mutate: updateCardCurrencyMargin, isLoading: isUpdatingMargin } =
    useMutation(client.cardConversionRates.updateCardCurrencyMargin, {
      onSuccess: () => {
        setEditingId(null);
        setEditingValue("");
        getCurrencyConversionRateslist(searchParams); // ✅ refetch
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
        setIsAlertOpened(true);
      },
    });

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({
      ...searchParams,
      page: page,
    });
  };

  useEffect(() => {
    if (!isMounted || !canViewConversions) {
      return;
    }
    getCurrencyConversionRateslist({
      ...searchParams,
    });
  }, [isMounted, canViewConversions, searchParams.page]);

  const columns = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      key: "id",
      render: (value: any) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Merchant ID
        </p>
      ),
      dataIndex: "merchant_id",
      key: "merchant_id",
      render: (value: any) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Source currency
        </p>
      ),
      dataIndex: "src_currency_code",
      key: "src_currency_code",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Destination currency
        </p>
      ),
      dataIndex: "dst_currency_code",
      key: "dst_currency_code",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Rate
        </p>
      ),
      dataIndex: "rate",
      key: "rate",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Margin percentage
        </p>
      ),
      dataIndex: "margin_percent",
      key: "margin_percent",
      render: (value: any, record: any) => {
        const isEditing = editingId === record.id;

        if (!canEdit) {
          return <span>{value}</span>;
        }

        return isEditing ? (
          <div className="flex items-center gap-2">
            <input
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              className="border px-2 py-1 rounded w-[80px]"
            />

            <GreenTickIcon
              className={`text-green-600 ${
                isUpdatingMargin ? "opacity-50 cursor-not-allowed" : ""
              }`}
              onClick={() => {
                updateCardCurrencyMargin({
                  currency_rate_id: record.id,
                  margin_percent: editingValue,
                });
              }}
            />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span>{value}</span>

            <button
              disabled={isUpdatingMargin}
              className="text-blue-500"
              onClick={() => {
                setEditingId(record.id);
                setEditingValue(value);
              }}
            >
              <EditIcon className="w-[20px] h-[20px] text-primary" />
            </button>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Final Rate
        </p>
      ),
      dataIndex: "final_rate",
      key: "final_rate",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Updated At
        </p>
      ),
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {moment(value).format("YYYY-MM-DD HH:mm:ss")}
        </p>
      ),
    },
  ];

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div>
          <h1 className="text-[22px] sm:text-[28px] font-manrope text-primary font-medium leading-tight">
            Currency Conversion Rate
          </h1>
        </div>
        <div className="pr-2">
          {" "}
          {/* 👈 adds right padding */}
          <button
            disabled={!canEdit || isLoadingSync}
            title={!canEdit ? "Read only - No edit permission" : undefined}
            className={`flex items-center gap-2 px-4 py-2 border rounded-md ${
              !canEdit || isLoadingSync
                ? "bg-gray-100 opacity-50 cursor-not-allowed"
                : "bg-white hover:bg-[#F3F4F6]"
            }`}
            onClick={() => {
              if (!canEdit) {
                setAlertText("You do not have permission to sync conversion rates");
                setIsAlertOpened(true);
                return;
              }
              syncConversionCurrencyRates({});
            }}
          >
            <span className="font-manrope text-[14px] font-medium text-primary">
              {isLoadingSync ? "Syncing..." : "Sync"}
            </span>
          </button>
        </div>
      </div>{" "}
      {!canViewConversions ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view conversion rates.
          </p>
        </div>
      ) : (
        <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
          <Table
            loading={isLoading}
            columns={columns}
            dataSource={dataSource}
            pagination={{
              total,
              current: searchParams.page,
              onChange: onPageChange,
              position: ["bottomCenter"],
              defaultPageSize: LIST_PAGE_SIZE,
              showSizeChanger: false,
            }}
            rowClassName="editable-row"
            // scroll={{ x: "max-content" }}
          />
        </div>
      )}
    </div>
  );
};

export default CardConversionHistory;
